import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { TextPopupComponent } from '../../basic/text-popup/text-popup.component';
import { BoardHolderService } from '../../core/board/board.holder.service';
import { HubSpotService } from '../../core/hubspot/hubspot.service';
import { InitDataService } from '../../core/init-data/init-data.service';
import { initPersonalityData } from '../../core/personality/init-personality';
import { PersonalityService } from '../../core/personality/personality.service';
import { ReportTimeInterval } from '../../core/report/report';
import { SlackService } from '../../core/slack/slack.service';
import { UserClerkService } from '../../core/user/user-clerk.service';
import { initSalesCycleData } from '../../core/workspace/init-workspace.data';
import { HourlyTaskStatus, HourlyTaskType, SalesCycle, SendWeeklyReportPayload } from '../../core/workspace/workspace';
import { SyncCompaniesHubspotComponent } from '../../domain/hubspot/sync-companies-hubspot/sync-companies-hubspot.component';
import { SyncCustomersHubspotComponent } from '../../domain/hubspot/sync-customers-hubspot/sync-customers-hubspot.component';
import { SalesCycleBarComponent } from '../../domain/sales-cycle/sales-cycle-bar/sales-cycle-bar.component';
import { WorkspaceListComponent } from '../../domain/workspace/workspace-list/workspace-list.component';
import { Id } from '../../shared/id/id';
import { EnumUtils } from '../../shared/utils/enum-utils';
import { BasicPageComponent } from '../_shared/basic-page/basic-page.component';
import { MainSidebarComponent } from '../_shared/main-sidebar/main-sidebar.component';
import { WorkspaceHolderService } from './../../core/workspace/workspace.holder.service';

@Component({
  selector: 'app-project-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzStepsModule,
    NzButtonModule,
    NzIconModule,
    NzSkeletonModule,
    NzInputModule,
    NzModalModule,
    NzTableModule,
    NzPopoverModule,
    NzRadioModule,
    NzTagModule,

    SyncCompaniesHubspotComponent,
    SyncCustomersHubspotComponent,
    BasicPageComponent,
    MainSidebarComponent,
    SalesCycleBarComponent,
    WorkspaceListComponent,
    TextPopupComponent,
  ],
  templateUrl: './project-settings.component.html',
  styleUrl: './project-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectSettingsComponent {
  isSyncCompaniesHubspotVisible = false;
  isSyncCustomersHubspotVisible = false;

  HourlyTaskStatus = HourlyTaskStatus;

  constructor(
    private messageService: NzMessageService,
    private personalityService: PersonalityService,
    private workspaceHolderService: WorkspaceHolderService,
    private hubspotService: HubSpotService,
    private slackService: SlackService,
    private boardHolderService: BoardHolderService,
    private initDataservice: InitDataService,
    private userClerkService: UserClerkService
  ) {
    effect(
      () => {
        const workspace = this.workspace();
        this.selectedSalesCycle.set(workspace.settings?.salesCycleList[0]);
      },
      {
        allowSignalWrites: true,
      }
    );
  }

  workspace = computed(() => this.workspaceHolderService.currentWorkspace());

  onSaveClick() {
    this.workspaceHolderService.updateCurrentWorkspace$().pipe(take(1)).subscribe();
  }

  selectedSalesCycle = signal<SalesCycle | undefined>(undefined);
  onSalesCycleChange(salesCycle: SalesCycle) {
    this.selectedSalesCycle.set(salesCycle);
  }

  isConfigSalesCycleModalVisible = false;

  onConfigSalesCycleClick() {
    this.isConfigSalesCycleModalVisible = true;
  }

  onInitSalesCycleClick() {
    const workspace = this.workspaceHolderService.currentWorkspace();

    if (!workspace.settings) {
      workspace.settings = { salesCycleList: [] };
    }

    workspace.settings!.salesCycleList = initSalesCycleData;

    this.workspaceHolderService.updateCurrentWorkspace$().pipe(take(1)).subscribe();
  }

  onDeleteSaleCycleClick(i: number) {
    // data.id
    const list = this.workspace().settings!.salesCycleList;
    list.splice(i, 1);

    this.workspace().settings!.salesCycleList = [...list];
  }

  onAddSaleCycleClick() {
    const list = this.workspace().settings!.salesCycleList;

    this.workspace().settings!.salesCycleList = [
      ...list,
      {
        id: 'id',
        title: 'sales-cycle-name',
        description: 'sales-cycle-description',
      },
    ];
  }

  onSaveConfigSalesCycleClick() {
    this.onSaveClick();
    this.isConfigSalesCycleModalVisible = false;
  }

  onCancelConfigSalesCycleClick() {
    this.isConfigSalesCycleModalVisible = false;
  }

  onInitPersonalityClick() {
    this.personalityService
      .itemList$()
      .pipe(take(1))
      .subscribe((list) => {
        if (list.length > 0) {
          this.messageService.error('No need to init personality.', { nzDuration: 5000 });
          return;
        }
        this.personalityService.addList$(initPersonalityData).pipe(take(1)).subscribe();
      });
  }

  //#region HubSpot
  isConnectedToHubSpot = toSignal(this.hubspotService.isConnected$());
  isHubSpotVisible = false;
  connectHubSpotDescriptionMarkdown = `
  ### Please select the following scropes:
  - [Read] crm.objects.companies
  - [Read] crm.objects.contacts 
  `;
  onConnectHubSpotClick(text: string) {
    this.workspaceHolderService
      .updateHubSpot$(text)
      .pipe(take(1))
      .subscribe(() => {
        this.isHubSpotVisible = false;
        this.messageService.success('Connect HubSpot successfully.');
      });
  }
  onConnectHubSpotCancel() {
    this.isHubSpotVisible = false;
  }
  //#endregion HubSpot

  //#region Slack
  isConnectedToSlack = toSignal(this.slackService.isConnected$());
  isSlackVisible = false;
  connectSlackDescriptionMarkdown = `
  ### Please select the following Bot Token Scopes:
  - chat:write
  - chat:write.public 
  `;
  onConnectSlackClick(text: string) {
    this.slackService
      .updateBotToken$(text)
      .pipe(take(1))
      .subscribe(() => {
        this.isSlackVisible = false;
        this.messageService.success('Connect Slack successfully.');
      });
  }
  onConnectSlackCancel() {
    this.isSlackVisible = false;
  }
  //#endregion Slack

  onSyncCompaniesClick() {
    this.isSyncCompaniesHubspotVisible = true;
  }

  onSyncCustomersClick() {
    this.isSyncCustomersHubspotVisible = true;
  }

  //#region Report
  getWeeklyReport = computed(() => {
    const workspace = this.workspace();
    const result = workspace.hourlyTaskList?.find((c) => c.taskType === HourlyTaskType.sendWeeklyReport);

    if (!result) {
      const tempHourlyTask = {
        id: Id.uuid.generateInBase62(),
        taskType: HourlyTaskType.sendWeeklyReport,
        payload: {
          sendToEmailList: [],
        },
        scheduledTimeStr: DateTime.utc().toISO(),
        status: HourlyTaskStatus.paused,
      };

      if (!workspace.hourlyTaskList) {
        workspace.hourlyTaskList = [];
      }

      workspace.hourlyTaskList.push(tempHourlyTask);

      this.workspaceHolderService.updateHourlyTaskList$().pipe(take(1)).subscribe();

      return tempHourlyTask;
    }

    return result;
  });

  onSendWeeklyReportClick() {
    this.workspaceHolderService.sendWeeklyReport$(this.getWeeklyReport()).pipe(take(1)).subscribe();
  }

  getSendToEmailList = computed(() => {
    const weeklyReport = this.getWeeklyReport();
    const result = (weeklyReport?.payload as SendWeeklyReportPayload)?.sendToEmailList ?? [];

    return result;
  });

  listOfReportData = [{}];

  timeInterval = ReportTimeInterval.daily;
  reportTimeIntervalValueList = EnumUtils.getAllValues(ReportTimeInterval);

  isAddEmailVisible = false;

  onAddEmailCancel() {
    this.isAddEmailVisible = false;
  }

  onRemoveEmailClick(index: number) {
    this.getSendToEmailList().splice(index, 1);

    console.log('onRemoveEmailClick:', this.workspaceHolderService.currentWorkspace());

    this.workspaceHolderService.updateHourlyTaskList$().pipe(take(1)).subscribe();
  }

  onAddEmailClick(text: string) {
    const payload = this.getWeeklyReport().payload as SendWeeklyReportPayload;

    if (!payload.sendToEmailList) {
      payload.sendToEmailList = [];
    }

    payload.sendToEmailList.push(text);

    this.workspaceHolderService
      .updateHourlyTaskList$()
      .pipe(take(1))
      .subscribe(() => {
        this.isAddEmailVisible = false;
      });
  }

  onRunningClick() {
    this.getWeeklyReport().status = HourlyTaskStatus.paused;

    this.workspaceHolderService
      .updateHourlyTaskList$()
      .pipe(take(1))
      .subscribe(() => {});
  }

  onPausedClick() {
    this.getWeeklyReport().status = HourlyTaskStatus.running;
    this.getWeeklyReport().scheduledTimeStr = DateTime.utc().toISO();

    this.workspaceHolderService
      .updateHourlyTaskList$()
      .pipe(take(1))
      .subscribe(() => {});
  }

  //#endregion Report
}
